import React, { useState, useEffect, useCallback } from 'react'
import { Form } from 'react-bootstrap'
import Header from '../Component/Header'
import PageHead from '../Component/pageHead'
import Footer from '../Component/footer'
import { PostAction } from '../Component/PostAction'
import { useFormik } from 'formik'
import { useHistory } from 'react-router-dom'

function sessionVal(key) {
  const raw = sessionStorage.getItem(key)
  if (raw == null || raw === 'null' || raw === 'undefined') return ''
  return raw
}

function mapUserToForm(user = {}) {
  return {
    id: user.id != null ? String(user.id) : '',
    firstName: user.first_name ?? user.firstName ?? '',
    lastName: user.last_name ?? user.lastName ?? '',
    email: user.email_address ?? user.email ?? '',
    mobile: user.mobile != null ? String(user.mobile) : '',
    panNumber: user.pan_number ?? user.panNumber ?? '',
    address1: user.address1 ?? user.address_1 ?? '',
  }
}

function formFromSession() {
  return mapUserToForm({
    id: sessionVal('id'),
    first_name: sessionVal('first_name'),
    last_name: sessionVal('last_name'),
    email_address: sessionVal('email_address') || sessionVal('email'),
    mobile: sessionVal('mobile'),
    pan_number: sessionVal('pan_number'),
    address1: sessionVal('address1'),
  })
}

function syncSessionFromForm(values) {
  const pairs = {
    id: values.id,
    first_name: values.firstName,
    last_name: values.lastName,
    email_address: values.email,
    email: values.email,
    mobile: values.mobile,
    pan_number: values.panNumber,
    address1: values.address1,
    name: `${values.firstName || ''} ${values.lastName || ''}`.trim(),
  }
  Object.entries(pairs).forEach(([key, value]) => {
    sessionStorage.setItem(key, value != null ? String(value) : '')
  })
}

function Profile() {
  const [formData, setFormData] = useState(formFromSession)
  const [loading, setLoading] = useState(true)
  const postUrl = 'user/updateProfile'
  const history = useHistory()

  useEffect(() => {
    if (!sessionStorage.getItem('id')) {
      history.push('/Login')
    }
  }, [history])

  const applyUser = useCallback((user) => {
    const mapped = mapUserToForm(user)
    setFormData(mapped)
    syncSessionFromForm(mapped)
  }, [])

  useEffect(() => {
    const userId = sessionStorage.getItem('id')
    if (!userId) {
      setLoading(false)
      return
    }

    const loadProfile = async () => {
      setLoading(true)
      try {
        const res = await PostAction('post', { id: userId }, 'user/userDetails')
        const user = res?.response
        if (res?.status === 'success' && user && typeof user === 'object') {
          applyUser(user)
        } else {
          setFormData(formFromSession())
        }
      } catch (e) {
        setFormData(formFromSession())
      } finally {
        setLoading(false)
      }
    }

    loadProfile()
  }, [applyUser])

  const { values, handleChange, handleSubmit } = useFormik({
    initialValues: formData,
    enableReinitialize: true,
    onSubmit: (values) => {
      const pan = String(values.panNumber || '').trim().toUpperCase()
      if (!pan) {
        alert('Please enter your PAN number.')
        return
      }
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) {
        alert('Enter a valid PAN number (e.g. ABCDE1234F).')
        return
      }
      const callPOSTAPI = async () => {
        const payload = {
          id: values.id,
          actor_id: values.id,
          first_name: values.firstName,
          last_name: values.lastName,
          email_address: values.email,
          mobile: values.mobile,
          pan_number: pan,
          address1: values.address1,
        }
        const res = await PostAction('post', payload, postUrl)
        if (res?.status === 'success') {
          syncSessionFromForm(values)
        }
        alert(res?.message || (res?.status === 'success' ? 'Profile updated.' : 'Update failed.'))
      }
      callPOSTAPI()
    },
  })

  return (
    <>
      <Header />
      <PageHead title="Profile" />
      <section className="profile-page">
        <div className="container">
          <div className="profile-card">
            <div className="profile-card-header">
              <h2 className="profile-card-title">Member details</h2>
              <p className="profile-card-subtitle">Keep your contact information up to date.</p>
            </div>
            {loading ? (
              <p className="text-muted mb-0">Loading your profile…</p>
            ) : (
              <Form onSubmit={handleSubmit} className="profile-form">
                <div className="row">
                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label" htmlFor="firstName">First Name</label>
                    <input
                      className="form-control"
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={values.firstName || ''}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label" htmlFor="lastName">Last Name</label>
                    <input
                      className="form-control"
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={values.lastName || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label" htmlFor="email">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      id="email"
                      value={values.email || ''}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label" htmlFor="mobile">Mobile</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="form-control"
                      name="mobile"
                      id="mobile"
                      value={values.mobile || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="panNumber">
                    PAN Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="panNumber"
                    id="panNumber"
                    value={values.panNumber || ''}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    maxLength={10}
                    placeholder="ABCDE1234F"
                    autoComplete="off"
                    style={{ textTransform: 'uppercase' }}
                  />
                  <div className="form-text">Required. Format: ABCDE1234F</div>
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="address1">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address1"
                    id="address1"
                    value={values.address1 || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="profile-actions">
                  <button className="btn profile-submit-btn" type="submit">Update Profile</button>
                </div>
              </Form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default Profile
